import { cn } from '@dfh-corp-sites/ui/utils'
import React, { FC, useEffect, useState } from 'react'

import {
  ConfigurableProductDetailsFragment,
  SimpleProductDetailsFragment,
} from '../../../../graphql/magento'
import {
  MagentoProductImage,
  SanityOrMagentoProductImage,
  SanityProductImagesHierarchy,
} from '../../../../lib/products/images'
import ItemImage, { ItemImageSourceFactory } from './ItemImage'
import ItemImagePreviews, { ItemImagePreviewSourceFactory } from './ItemImagePreviews'

import productPlaceholderImage from '../../../../img/product-image-placeholder.png'

export interface ItemImagesProps {
  className?: string
  previewClassName?: string
  selectedImageClassName?: string
  /** Factory function to generate image preview sources when component used elsewhere */
  imagePreviewSourceFactory?: ItemImagePreviewSourceFactory
  /** Factory function to generate image sources when component used elsewhere */
  imageSourceFactory?: ItemImageSourceFactory
  item: ConfigurableProductDetailsFragment
  selectedVariant: SimpleProductDetailsFragment | null | undefined
  sanityImages: SanityProductImagesHierarchy
  size?: 'sm' | 'lg'
}

function isMagentoProductImage(
  image:
    | ConfigurableProductDetailsFragment['image']
    | NonNullable<ConfigurableProductDetailsFragment['media_gallery']>[number],
): image is MagentoProductImage {
  return !!image && image.url != null
}

const selectDefaultImage = (
  item: ConfigurableProductDetailsFragment,
  sanityImages: SanityProductImagesHierarchy,
): SanityOrMagentoProductImage | undefined =>
  // prefer main image from Sanity if available
  sanityImages.parent?.mainImage?.asset
    ? sanityImages.parent.mainImage
    : isMagentoProductImage(item.image)
      ? item.image
      : undefined

const selectDefaultImageGallery = (
  item: ConfigurableProductDetailsFragment,
  sanityImages: SanityProductImagesHierarchy,
): ReadonlyArray<SanityOrMagentoProductImage> =>
  // prefer images from Sanity if available
  sanityImages.parent?.mainImage?.asset
    ? [sanityImages.parent.mainImage]
    : item.media_gallery?.filter(isMagentoProductImage) ?? []

const placeholderImage: MagentoProductImage = {
  url: productPlaceholderImage,
  label: '',
}

const ItemImages: FC<ItemImagesProps> = ({
  className,
  imagePreviewSourceFactory,
  imageSourceFactory,
  item,
  selectedVariant = null,
  sanityImages,
  previewClassName,
  selectedImageClassName,
  size,
}) => {
  const [selectedImage, setSelectedImage] = useState<
    SanityOrMagentoProductImage | null | undefined
  >(() => selectDefaultImage(item, sanityImages))
  const [images, setImages] = useState<ReadonlyArray<SanityOrMagentoProductImage>>(() =>
    selectDefaultImageGallery(item, sanityImages),
  )

  // update selected image and images collection when selected variant changes
  useEffect(() => {
    if (selectedVariant?.sku) {
      // select images for selected variant
      const fromSanity = sanityImages.children.get(selectedVariant.sku)
      if (fromSanity && fromSanity.mainImage) {
        // prefer Sanity images if available
        setSelectedImage(fromSanity.mainImage)
        setImages([fromSanity.mainImage, ...fromSanity.otherImages])
      } else if (selectedVariant.image && isMagentoProductImage(selectedVariant.image)) {
        // fallback to Magento image if available
        setSelectedImage(selectedVariant.image)
        setImages(selectedVariant.media_gallery?.filter(isMagentoProductImage) ?? [])
      } else {
        setSelectedImage(placeholderImage)
        setImages([placeholderImage])
      }
    } else {
      // select images for parent product
      if (sanityImages.parent.mainImage) {
        // prefer Sanity images if available
        setSelectedImage(sanityImages.parent.mainImage)
        setImages([sanityImages.parent.mainImage])
      } else if (item.image && isMagentoProductImage(item.image)) {
        // fallback to Magento image if available
        setSelectedImage(item.image)
        setImages(item.media_gallery?.filter(isMagentoProductImage) ?? [])
      } else {
        setSelectedImage(placeholderImage)
        setImages([placeholderImage])
      }
    }
  }, [item, selectedVariant, sanityImages])

  const showItemImagePreviews = (images?.length ?? 0) > 1

  return (
    <div className={cn('space-y-2.5', className)}>
      {selectedImage && (
        <ItemImage
          size={size}
          image={selectedImage}
          imageSourceFactory={imageSourceFactory}
          showItemImagePreviews={showItemImagePreviews}
          className={selectedImageClassName}
        />
      )}
      <ItemImagePreviews
        size={size}
        className={previewClassName}
        images={images || []}
        imagePreviewSourceFactory={imagePreviewSourceFactory}
        selectedImage={selectedImage}
        setSelectedImage={setSelectedImage}
        showItemImagePreviews={showItemImagePreviews}
      />
    </div>
  )
}

export default ItemImages
